// ConvertToContractModal.jsx
import React, { useMemo, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  MenuItem,
  TextField,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import useConvertToContract from '../CustomHooks/useConvertToContract';
import FileUpload from './FileUpload';
import customAxios, { customFormAxios } from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import { updateRecordModifiedDate } from 'routes/Pages/util/CommonAPIs';

// ── Constants ─────────────────────────────────────────────────────────────────
const yesNoOption = ['Yes', 'No'];

const ForestSourcingAreasSelect = [
  'Antrim',
  'Armagh',
  'Carlow',
  'Cavan',
  'Clare',
  'Cork',
  'Derry',
  'Donegal',
  'Down',
  'Dublin',
  'Fermanagh',
  'Galway',
  'Kerry',
  'Kildare',
  'Kilkenny',
  'Laois',
  'Leitrim',
  'Limerick',
  'Longford',
  'Louth',
  'Mayo',
  'Meath',
  'Monaghan',
  'Offaly',
  'Roscommon',
  'Sligo',
  'Tipperary',
  'Tyrone',
  'Waterford',
  'Westmeath',
  'Wexford',
  'Wicklow',
];
const SupplierTypeSelect = ['Sawmill', 'Forest', 'Hedgerow', 'Site Clearanc', 'Storage', 'N/A'];
const ContractWithSelect = ['ECC', 'Murrays', 'Coillte', 'SWS', 'IFU', 'Private', 'Other'];
const HarvestedBySelect = ['WTG', 'ECC', 'Murrays', 'Other'];

// ── Small helpers ─────────────────────────────────────────────────────────────
const fmt = val => (val != null && !isNaN(val) ? Number(val).toFixed(2) : '—');

/** Accordion header with a ✓ / ⚠ completion icon */
const SectionHeader = ({ title, complete }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%', pr: 1 }}>
    <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
    <Box sx={{ flexGrow: 1 }} />
    {complete === true && <CheckCircleIcon fontSize="small" color="success" />}
    {complete === false && <ErrorOutlineIcon fontSize="small" color="warning" />}
  </Stack>
);

/** Minimal single-file upload slot used for the mandatory signed contract */
const SingleFileSlot = ({ label, hint, file, onUpload, onRemove, error }) => {
  const inputId = `sfc-${label.replace(/\s/g, '')}`;
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
        <Chip
          size="small"
          label="Required"
          color={file ? 'success' : 'warning'}
          sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
        />
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {hint}
        </Typography>
      )}

      {file ? (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ p: 1, border: '1px solid', borderColor: 'success.light', borderRadius: 1, bgcolor: 'success.50' }}>
          <UploadFileIcon fontSize="small" color="success" />
          <Typography variant="body2" sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
            {file.name} {/* FIX: Changed from file.docName to file.name */}
          </Typography>
          <IconButton size="small" onClick={onRemove} color="error">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : (
        <>
          <input
            id={inputId}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={e => onUpload(e.target.files)}
          />
          <label htmlFor={inputId}>
            <Box
              component="span"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                border: '2px dashed',
                borderColor: error ? 'warning.main' : 'grey.300',
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}>
              <UploadFileIcon fontSize="small" color={error ? 'warning' : 'action'} />
              <Typography variant="body2" color={error ? 'warning.dark' : 'text.secondary'}>
                Click to upload (PDF, JPG, PNG)
              </Typography>
            </Box>
          </label>
        </>
      )}
    </Box>
  );
};
// ── Main component ────────────────────────────────────────────────────────────
const ConvertToContractModal = ({ open, onClose, contractRowData }) => {
  const {
    selectedContractType,
    setSelectedContractType,
    selectedpurchaseType,
    setSelectedpurchaseType,
    fellingLicenceNo,
    lableOfContract,
    setLableOfContract,
    isProductSustainable,
    setIsProductSustainable,
    forestSourcingArea,
    setForestSourcingArea,
    supplierType,
    setSupplierType,
    validSelfDeclaration,
    setValidSelfDeclaration,
    biomassSite,
    setBiomassSite,
    forestGrower,
    setForestGrower,
    contractWith,
    setContractWith,
    harvestedBy,
    setHarvestedBy,
    tripartiteAgreement,
    setTripartiteAgreement,
    certificationNote,
    setCertificationNote,
    agreedRates,
    setAgreedRates, // coillteRoadsideRate, shortStakesPremium, coillteRateConfirmedBy
    productPricing,
    vatRate,
    setVatRates,
    otherVatRate,
    setOtherVatRate,
    internalNotes,
    setInternalNotes,
    growerNotes,
    setGrowerNotes,
    contractStartDate,
    setContractStartDate,
    contractEndDate,
    setContractEndDate,
    uploadedFiles,
    handleFileUpload,
    removeFile,
    signedContractFile,
    handleSignedContractUpload,
    removeSignedContractFile,
    notificationContact,
    handleNotificationContactChange,
    accountDetails,
    handleAccountChange,
    questionnaire,
    handleQuestionnaireChange,
    siteInfoData,
    sendContractNotification,
  } = useConvertToContract(contractRowData?.current, contractRowData?.current?.siteAssessmentID);

  const { showToast } = useToast();

  // All sections open by default — no forced clicks
  const [expanded, setExpanded] = useState({
    certification: true,
    siteInfo: true,
    rates: true,
    files: true,
    contacts: true,
  });
  const toggle = key => (_, isExpanded) => setExpanded(prev => ({ ...prev, [key]: isExpanded }));

  // ── Validation ─────────────────────────────────────────────────────────────
  const hasNotificationContact = useMemo(() => {
    return Boolean(notificationContact?.email?.trim() || notificationContact?.phone?.trim());
  }, [notificationContact]);

  const validateGrowerEmail = value => {
    if (!value) return true; // Allow empty (handled by hasNotificationContact)
    const email = value.trim().toLowerCase();
    const worrellDomains = ['worrellharvesting.com', 'worrellharvesting.ie', 'worrellharvesting.co.uk'];
    return !worrellDomains.some(domain => email.endsWith(`@${domain}`));
  };

  const hasSignedContract = Boolean(signedContractFile);

  const certificationComplete = useMemo(() => {
    if (!isProductSustainable) return null;
    return Boolean(
      forestSourcingArea &&
        supplierType &&
        validSelfDeclaration &&
        biomassSite &&
        contractWith &&
        harvestedBy &&
        tripartiteAgreement,
    );
  }, [
    isProductSustainable,
    forestSourcingArea,
    supplierType,
    validSelfDeclaration,
    biomassSite,
    contractWith,
    harvestedBy,
    tripartiteAgreement,
  ]);

  const validationErrors = useMemo(() => {
    const errors = [];
    if (!hasNotificationContact) errors.push({ key: 'contacts', label: 'Add a notification email or phone' });
    if (!hasSignedContract) errors.push({ key: 'files', label: 'Upload the signed grower contract' });
    return errors;
  }, [hasNotificationContact, hasSignedContract]);

  const isFormValid = validationErrors.length === 0;

  const jumpTo = key => {
    setExpanded(prev => ({ ...prev, [key]: true }));
    setTimeout(() => {
      const el = document.getElementById(`section-${key}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const IRISH_PHONE_REGEX = /^\+353\d{1,3}\d{3}\d{4}$/;

  const validateIrishPhone = value => {
    if (!value) return false;
    const cleaned = value.replace(/\s+/g, '');
    return IRISH_PHONE_REGEX.test(cleaned);
  };

  /** Strip spaces — what we send to the backend */
  const formatPhoneForBackend = value => (value ? value.replace(/\s+/g, '') : '');

  // Put this above handleConvertToContractClick, or in a utils file
  const notificationSections = ['DocketLoad', 'TruckEntering', 'DocketUnloading'];

  function buildNotificationRequests({ email, cleanPhone, isPhonePopulated, contractUniqueID, sections }) {
    const requests = [];

    sections.forEach(section => {
      if (email?.trim()) {
        requests.push(
          customAxios.post(
            '/Ops/AddContractsUsersToNotify',
            {
              contractUniqueID,
              userId: 0,
              notificationType: 'EMAIL',
              emailOrPhone: email.trim(),
              notificationSection: section,
            },
            { headers: authHeader() },
          ),
        );
      }

      if (isPhonePopulated) {
        requests.push(
          customAxios.post(
            '/Ops/AddContractsUsersToNotify',
            {
              contractUniqueID,
              userId: 0,
              notificationType: 'SMS',
              emailOrPhone: cleanPhone,
              notificationSection: section,
            },
            { headers: authHeader() },
          ),
        );
      }
    });

    return requests;
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleConvertToContractClick() {
    if (contractRowData.current.contractDoneFlag) {
      showToast(`Contract already created for ${contractRowData.current.pricingVersion}`);
      return;
    }
    if (!isFormValid) {
      showToast('Please complete all required fields.', 'error');
      return;
    }

    // ── NEW: Explicit Phone Validation Safeguard ─────────────────────────────
    const cleanPhone = formatPhoneForBackend(notificationContact.phone);
    const isPhonePopulated = cleanPhone && cleanPhone !== '+353';

    if (isPhonePopulated && !validateIrishPhone(notificationContact.phone)) {
      showToast('Please enter a valid Irish phone number (+353 XX XXX XXXX).', 'error');
      return;
    }
    // ─────────────────────────────────────────────────────────────────────────

    try {
      // 1. Create the contract record
      const finalResponse = await customAxios.post(
        '/Ops/ConvertTimberPricingToContract',
        {
          fellingLicenseNo: fellingLicenceNo,
          siteAssessmentID: contractRowData.current.siteAssessmentID,
          pricingVersion: contractRowData.current.pricingVersion,
        },
        { headers: authHeader() },
      );

      if (finalResponse.status !== 200) return;

      const { newContractUniqueID } = finalResponse.data;
      if (!newContractUniqueID) {
        showToast('Contract already exists for this site assessment!', 'error');
        return;
      }

      showToast('Converting to contract…');

      await updateRecordModifiedDate(contractRowData.current.siteAssessmentID);

      // 2. Fetch the freshly created contract record so we can patch it
      const createdContract = await customAxios.get(
        `/Ops/GetContracts?flagActive=1&contractUniqueID=${newContractUniqueID}`,
        { headers: authHeader() },
      );
      const contractData = createdContract.data?.[0] ?? {};

      // Build notification requests once, up front
      const notificationBatch = buildNotificationRequests({
        email: notificationContact.email,
        cleanPhone,
        isPhonePopulated,
        contractUniqueID: newContractUniqueID,
        sections: notificationSections,
      });

      // 3. Fire all save calls concurrently
      await Promise.all([
        customAxios.post(
          '/Ops/EditContract',
          {
            ...contractData,
            contractUniqueID: newContractUniqueID,
            contractType: selectedContractType || siteInfoData.siteAssessmentType,
            purchaseType: selectedpurchaseType || siteInfoData.siteAssessmentCategory,
            startDate: contractStartDate || null,
            finishDate: contractEndDate || null,
            notestoOperators: growerNotes,
            wH_NumberName: lableOfContract,
          },
          { headers: authHeader() },
        ),

        customAxios.post(
          '/Ops/AddContractCertificationInfo',
          {
            contractUniqueID: newContractUniqueID,
            forestSourcingArea,
            supplierType,
            validSelfDeclaration,
            biomassSite,
            forestGrower,
            contractWith,
            harvestedBy,
            tripartiteAgreement,
            notes: certificationNote,
          },
          { headers: authHeader() },
        ),

        customAxios.post(
          '/Ops/AddContractsQuestionnaire',
          {
            contractUniqueID: newContractUniqueID,
            firstThinnings: questionnaire.firstThinning ? 'Yes' : 'No',
            secondThinnings: questionnaire.secondThinning ? 'Yes' : 'No',
            clearFell: questionnaire.clearFell ? 'Yes' : 'No',
            treesLeftPerHectareIfDiff: questionnaire.treesLeft || '',
            noteIfYouNeedDifferentQtys: questionnaire.differentQuantitiesNote || '',
            doYouRequireHardCopy: questionnaire.requireHardCopy || '',
            archaeologicalArea: questionnaire.archaeologicalArea ? 'Yes' : 'No',
            recreationalArea: questionnaire.recreationalArea ? 'Yes' : 'No',
            waterways: questionnaire.waterways ? 'Yes' : 'No',
            powerOrPhoneLines: questionnaire.powerPhoneLines ? 'Yes' : 'No',
            railLines: questionnaire.railLines ? 'Yes' : 'No',
            nearbyDwellings: questionnaire.nearbyDwellings ? 'Yes' : 'No',
            otherPleaseDetail: questionnaire.other ? questionnaire.otherDetails || '' : '',
            isPermitDocRequired: questionnaire.permitDocRequired ?? 'No',
            isSupplierDocRequired: questionnaire.supplierDocRequired ?? 'No',
          },
          { headers: authHeader() },
        ),

        customAxios.post(
          '/Ops/EditContractProductDetails',
          {
            contractUniqueID: newContractUniqueID,
            vatRate: vatRate === '' ? null : Number(vatRate),
          },
          { headers: authHeader() },
        ),

        customAxios.post(
          '/Ops/AddContractsAccountingInfo',
          {
            contractUniqueID: newContractUniqueID,
            accountHolderName: accountDetails.accountHolder,
            bankName: accountDetails.bankName,
            bic: accountDetails.bic,
            iban: accountDetails.iban,
          },
          { headers: authHeader() },
        ),

        // ── Dynamic Notification Requests ──────────────────────────────────
        ...notificationBatch,

        // FIXED: Only dispatches if there are numbers entered past the +353 prefix, and strips spaces
        ...(isPhonePopulated
          ? [
              customAxios.post(
                '/Ops/AddContractsUsersToNotify',
                {
                  contractUniqueID: newContractUniqueID,
                  userId: 0,
                  notificationType: 'SMS',
                  emailOrPhone: cleanPhone, // Uses sanitized whitespace-free string
                  notificationSection: 'New contract',
                },
                { headers: authHeader() },
              ),
            ]
          : []),

        // Coillte rates — only when sustainable
        ...(isProductSustainable
          ? [
              customAxios.post(
                '/Ops/AddContractAgreedRates',
                {
                  contractUniqueID: newContractUniqueID,
                  coillteRoadsideRate: agreedRates.coillteRoadsideRate || '',
                  shortStakesPremium: agreedRates.shortStakesPremium || '',
                  coillteRateConfirmedBy: agreedRates.coillteRateConfirmedBy || '',
                },
                { headers: authHeader() },
              ),
            ]
          : []),
      ]);

      // 4. Upload files
      try {
        const filesToUpload = [];

        if (signedContractFile) {
          filesToUpload.push(signedContractFile);
        }

        if (uploadedFiles.length > 0) {
          filesToUpload.push(...uploadedFiles);
        }

        const uploadPromises = filesToUpload.map(async item => {
          const formData = new FormData();
          formData.append('contractUniqueID', newContractUniqueID);
          formData.append('docName', item.file.name);
          formData.append('doc', item.file);
          formData.append('docType', item.docType);

          return customFormAxios.post('/Ops/AddContractsDocs', formData, {
            headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
          });
        });

        await Promise.all(uploadPromises);
      } catch (uploadErr) {
        console.error('File upload failed:', uploadErr);
        showToast('Contract saved but file upload failed — please re-upload docs manually.', 'warning');
      }

      // 5. Send notification AFTER everything is saved so the contract page has data
      await sendContractNotification(newContractUniqueID);

      showToast('Contract created successfully!', 'success');
      onClose();
    } catch (err) {
      console.error('Convert to contract error:', err);
      showToast('Something went wrong — please try again.', 'error');
    }
  }

  // ── Shared accordion sx ───────────────────────────────────────────────────
  const accordionSx = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px !important',
    mb: 2,
    '&:before': { display: 'none' },
  };

  return (
    <Modal
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          width: '95vw',
          maxWidth: '1100px',
          height: '90vh',
          maxHeight: '95vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Box
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: '2px solid',
            borderColor: 'primary.main',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Convert to Contract
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {siteInfoData.landownerName && `${siteInfoData.landownerName} · `}
              {siteInfoData.siteLocation && `${siteInfoData.siteLocation} · `}
              {siteInfoData.siteAssessmentType || 'Review all sections before converting'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ mt: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Validation banner ───────────────────────────────────────────── */}
        {validationErrors.length > 0 && (
          <Alert
            severity="warning"
            sx={{ borderRadius: 0 }}
            action={
              <Stack direction="row" spacing={0.5}>
                {validationErrors.map(err => (
                  <Button
                    key={err.key}
                    size="small"
                    color="inherit"
                    onClick={() => jumpTo(err.key)}
                    sx={{ fontWeight: 600, textDecoration: 'underline' }}>
                    Fix
                  </Button>
                ))}
              </Stack>
            }>
            {validationErrors.length === 1
              ? validationErrors[0].label
              : `${validationErrors.length} items still needed: ${validationErrors.map(e => e.label).join(' · ')}`}
          </Alert>
        )}

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          {/* ══════════ 1. Contract Type & Certification ══════════ */}
          <Accordion
            id="section-certification"
            elevation={0}
            sx={accordionSx}
            expanded={expanded.certification}
            onChange={toggle('certification')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionHeader title="Contract Type & Certification" complete={certificationComplete} />
            </AccordionSummary>
            <AccordionDetails>
              {/* Read-only type fields pulled from site assessment */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Type of Contract"
                    value={siteInfoData.siteAssessmentType || '—'}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    helperText="Sourced from site assessment"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Purchase Type"
                    value={siteInfoData.siteAssessmentCategory || '—'}
                    fullWidth
                    size="small"
                    InputProps={{ readOnly: true }}
                    helperText="Sourced from site assessment"
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Checkbox checked={isProductSustainable} onChange={e => setIsProductSustainable(e.target.checked)} />
                }
                label={<Typography sx={{ fontWeight: 600 }}>Is this product sustainable?</Typography>}
                sx={{ mb: isProductSustainable ? 2 : 0 }}
              />

              {isProductSustainable && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Certification Information
                  </Typography>
                  <Grid container spacing={1.5}>
                    {[
                      [
                        'Forest Sourcing Area (County)',
                        forestSourcingArea,
                        setForestSourcingArea,
                        ForestSourcingAreasSelect,
                      ],
                      ['Supplier Type', supplierType, setSupplierType, SupplierTypeSelect],
                      ['Valid Self Declaration', validSelfDeclaration, setValidSelfDeclaration, yesNoOption],
                      ['Biomass Site', biomassSite, setBiomassSite, yesNoOption],
                      ['Contract With', contractWith, setContractWith, ContractWithSelect],
                      ['Harvested By', harvestedBy, setHarvestedBy, HarvestedBySelect],
                      ['Tripartite Agreement', tripartiteAgreement, setTripartiteAgreement, yesNoOption],
                    ].map(([label, val, setter, opts]) => (
                      <Grid item xs={12} sm={6} md={4} key={label}>
                        <TextField
                          select
                          fullWidth
                          label={label}
                          value={val}
                          onChange={e => setter(e.target.value)}
                          size="small">
                          {opts.map(o => (
                            <MenuItem key={o} value={o}>
                              {o}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    ))}

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Forest Grower"
                        value={forestGrower}
                        onChange={e => setForestGrower(e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Felling License Number"
                        value={fellingLicenceNo}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>

                  <TextField
                    label="Certification Notes"
                    value={certificationNote}
                    onChange={e => setCertificationNote(e.target.value)}
                    fullWidth
                    size="small"
                    sx={{ mt: 2 }}
                  />
                </>
              )}
            </AccordionDetails>
          </Accordion>

          {/* ══════════ 2. Site Info & Questionnaire ══════════ */}
          <Accordion
            id="section-siteInfo"
            elevation={0}
            sx={accordionSx}
            expanded={expanded.siteInfo}
            onChange={toggle('siteInfo')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionHeader title="Site Info & Questionnaire" complete={null} />
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Site Information
              </Typography>
              <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Grid container spacing={1.5}>
                  {[
                    ['Landowner', siteInfoData.landownerName],
                    ['Address', siteInfoData.address],
                    ['Site Location', siteInfoData.siteLocation],
                    ['Total Area (ha)', siteInfoData.totalArea],
                    ['WH No', 'Auto-generated'],
                    ['Felling Lic No', siteInfoData.fellingLicenceNo],
                    ['Date', siteInfoData.date],
                  ].map(([label, value]) => (
                    <Grid item xs={6} sm={4} md={3} key={label}>
                      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', color: 'text.secondary' }}>
                        {label}
                      </Typography>
                      <Typography variant="body2">{value || '—'}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Questionnaire
              </Typography>

              <Grid container spacing={2}>
                {/* Left column */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Thinning / Fell Type
                  </Typography>
                  <FormGroup>
                    {[
                      ['firstThinning', "1st Thinning's (trees left 1500–1700)"],
                      ['secondThinning', "2nd Thinning's (trees left 1000–1200)"],
                      ['clearFell', 'Clear-fell'],
                    ].map(([key, label]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            size="small"
                            checked={questionnaire[key]}
                            onChange={e => handleQuestionnaireChange(key, e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">{label}</Typography>}
                      />
                    ))}
                  </FormGroup>

                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Trees left per hectare (if different)"
                      value={questionnaire.treesLeft}
                      onChange={e => handleQuestionnaireChange('treesLeft', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Note if different quantities needed"
                      value={questionnaire.differentQuantitiesNote}
                      onChange={e => handleQuestionnaireChange('differentQuantitiesNote', e.target.value)}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Other details"
                      value={questionnaire.otherDetails}
                      onChange={e => handleQuestionnaireChange('otherDetails', e.target.value)}
                    />
                  </Stack>
                </Grid>

                {/* Right column */}
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Environmental Areas
                  </Typography>
                  <FormGroup>
                    {[
                      ['archaeologicalArea', 'Archaeological area'],
                      ['recreationalArea', 'Recreational area'],
                      ['waterways', 'Waterways'],
                      ['powerPhoneLines', 'Power / phone lines'],
                      ['railLines', 'Rail lines'],
                      ['nearbyDwellings', 'Nearby dwellings'],
                    ].map(([key, label]) => (
                      <FormControlLabel
                        key={key}
                        control={
                          <Checkbox
                            size="small"
                            checked={questionnaire[key]}
                            onChange={e => handleQuestionnaireChange(key, e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">{label}</Typography>}
                      />
                    ))}
                  </FormGroup>

                  <Stack spacing={1} sx={{ mt: 2 }}>
                    {[
                      ['Hard copy left in forest?', 'requireHardCopy'],
                      ['Permit doc required?', 'permitDocRequired'],
                      ['Supplier doc required?', 'supplierDocRequired'],
                    ].map(([label, key]) => (
                      <Box key={key}>
                        <FormLabel sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{label}</FormLabel>
                        <RadioGroup
                          row
                          value={questionnaire[key]}
                          onChange={e => handleQuestionnaireChange(key, e.target.value)}>
                          <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                        </RadioGroup>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* ══════════ 3. Rates & Pricing ══════════ */}
          <Accordion id="section-rates" elevation={0} sx={accordionSx} expanded={expanded.rates} onChange={toggle('rates')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionHeader title="Rates & Pricing" complete={null} />
            </AccordionSummary>
            <AccordionDetails>
              {isProductSustainable && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Coillte Rates
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Coillte Roadside Rate"
                        value={agreedRates.coillteRoadsideRate}
                        onChange={e => setAgreedRates(p => ({ ...p, coillteRoadsideRate: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Short Stakes Premium"
                        value={agreedRates.shortStakesPremium}
                        onChange={e => setAgreedRates(p => ({ ...p, shortStakesPremium: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Confirmed By"
                        value={agreedRates.coillteRateConfirmedBy}
                        onChange={e => setAgreedRates(p => ({ ...p, coillteRateConfirmedBy: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Per-product pricing table — all destination + rate columns */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Product Pricing
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      {[
                        'Product',
                        'Destination',
                        'Haulage Rate',
                        'Harvesting Rate',
                        'Extraction Rate',
                        'Purchase Price',
                      ].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productPricing.map((item, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{item.product}</TableCell>
                        <TableCell>{item.destination || '—'}</TableCell>
                        <TableCell>{fmt(item.haulageRate)}</TableCell>
                        <TableCell>{fmt(item.harvestingRate)}</TableCell>
                        <TableCell>{fmt(item.extractionRate)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{fmt(item.purchasePrice)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ mb: 2 }} />

              {/* VAT */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <FormLabel sx={{ fontWeight: 600 }}>VAT Rate</FormLabel>
                <RadioGroup
                  row
                  value={otherVatRate ? '0' : vatRate}
                  onChange={e => {
                    const val = e.target.value;
                    if (val === '0') {
                      setOtherVatRate(true);
                      setVatRates('');
                    } else {
                      setOtherVatRate(false);
                      setVatRates(val);
                    }
                  }}>
                  <FormControlLabel value="5" control={<Radio size="small" />} label="Farmer 5.0%" />
                  <FormControlLabel value="23" control={<Radio size="small" />} label="23%" />
                  <FormControlLabel value="0" control={<Radio size="small" />} label="Other" />
                </RadioGroup>
                {otherVatRate && (
                  <TextField
                    label="VAT %"
                    type="number"
                    size="small"
                    value={vatRate}
                    onChange={e => setVatRates(e.target.value)}
                    sx={{ width: 130 }}
                  />
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* ══════════ 4. Files & Required Uploads ══════════ */}
          <Accordion id="section-files" elevation={0} sx={accordionSx} expanded={expanded.files} onChange={toggle('files')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionHeader title="Files & Required Uploads" complete={hasSignedContract} />
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Mandatory signed contract */}
                <Grid item xs={12} md={6}>
                  {console.log(signedContractFile)}
                  <SingleFileSlot
                    label="Signed Grower Contract"
                    hint="Upload the hard copy signed by WTG and the grower (PDF, scan or photo)."
                    file={signedContractFile?.file}
                    onUpload={handleSignedContractUpload}
                    onRemove={removeSignedContractFile}
                    error={!hasSignedContract}
                  />
                </Grid>

                {/* Other office docs */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Other Office Documents
                  </Typography>
                  <FileUpload uploadedFiles={uploadedFiles} onFileUpload={handleFileUpload} onRemoveFile={removeFile} />
                </Grid>

                {/* Contract label */}
                <Grid item xs={12}>
                  <TextField
                    label="Detailed label of contract"
                    value={lableOfContract}
                    onChange={e => setLableOfContract(e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* ══════════ 5. Contacts, Accounting & Notes ══════════ */}
          <Accordion
            id="section-contacts"
            elevation={0}
            sx={accordionSx}
            expanded={expanded.contacts}
            onChange={toggle('contacts')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <SectionHeader title="Contacts, Accounting & Notes" complete={hasNotificationContact} />
            </AccordionSummary>
            <AccordionDetails>
              {/* Notification contact — mandatory */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Notification Contact
                <Chip
                  size="small"
                  label="At least one required"
                  color={hasNotificationContact ? 'success' : 'warning'}
                  sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                />
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Grower Email"
                    type="email"
                    value={notificationContact?.email || ''}
                    onChange={e => handleNotificationContactChange('email', e.target.value)}
                    fullWidth
                    size="small"
                    error={!validateGrowerEmail(notificationContact?.email)}
                    helperText={
                      !validateGrowerEmail(notificationContact?.email)
                        ? 'Cannot use a Worrell Harvesting company email'
                        : !hasNotificationContact
                        ? 'Email or phone required'
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Grower Phone"
                    value={notificationContact?.phone || ''}
                    onChange={e => handleNotificationContactChange('phone', e.target.value)}
                    fullWidth
                    size="small"
                    error={!hasNotificationContact}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Account details */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Account Details (for payments)
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  ['Account Holder', 'accountHolder'],
                  ['Bank Name', 'bankName'],
                  ['IBAN', 'iban'],
                  ['BIC', 'bic'],
                ].map(([label, field]) => (
                  <Grid item xs={12} sm={6} md={3} key={field}>
                    <TextField
                      label={label}
                      size="small"
                      fullWidth
                      value={accountDetails[field]}
                      onChange={e => handleAccountChange(field, e.target.value)}
                    />
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ mb: 2 }} />

              {/* Notes & timeline */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Notes & Contract Timeline
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Internal notes (WH staff only)"
                    value={internalNotes}
                    onChange={e => setInternalNotes(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Notes for Grower / Owner"
                    value={growerNotes}
                    onChange={e => setGrowerNotes(e.target.value)}
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Start Date"
                    type="date"
                    value={contractStartDate}
                    onChange={e => setContractStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="End Date"
                    type="date"
                    value={contractEndDate}
                    onChange={e => setContractEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={2}
          px={3}
          py={1.5}
          sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          {!isFormValid && (
            <Typography variant="caption" color="warning.dark" sx={{ mr: 'auto' }}>
              Complete the required items above to enable conversion
            </Typography>
          )}
          <Button onClick={onClose} variant="outlined" size="medium">
            Cancel
          </Button>
          <Button
            onClick={handleConvertToContractClick}
            variant="contained"
            color="primary"
            size="medium"
            disabled={!isFormValid}>
            Convert to Contract
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConvertToContractModal;
